// 📄 pages/Admin/components/ApiTemplateCard.tsx
// Fila de "Plantillas de APIs" del catálogo: header con icono/nombre/
// badge de Data Center/Guardar/Eliminar/chevron, más el formulario
// (ApiTemplateForm.tsx) cuando está expandida. El alta de una plantilla
// nueva ya no pasa por acá — eso vive en NuevaPlantillaModal.tsx, que
// reutiliza el mismo ApiTemplateForm.
import { Trash2, ChevronDown, ChevronUp, Save, Loader2 } from "lucide-react";
import { ApiTemplate, ApiTemplateData } from "../../../services/catalogoApisService.ts";
import { TIPOS_API } from "../../../components/Configuracion/apiTiposConfig.ts";
import ApiTemplateForm from "./ApiTemplateForm.tsx";

interface ApiTemplateCardProps {
  template: ApiTemplate;
  expandida: boolean;
  guardando: boolean;
  onToggleExpandida: () => void;
  onChange: (data: ApiTemplateData) => void;
  onGuardar: () => void;
  onEliminar: () => void;
}

// Mismas etiquetas que ApiTemplateForm.tsx (badge del header en vez del
// select) — ver comentario ahí sobre por qué difieren de
// ARIBA_DATA_CENTERS.
const DATA_CENTER_LABELS: Record<string, string> = { US: "USA (EE. UU.)", EU: "Europa (EU)" };
const dataCenterLabel = (codigo: string) => DATA_CENTER_LABELS[codigo] || codigo;

export default function ApiTemplateCard({ template, expandida, guardando, onToggleExpandida, onChange, onGuardar, onEliminar }: ApiTemplateCardProps) {
  const tipoMeta = TIPOS_API[template.tipo] || TIPOS_API.ariba;

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 p-5">
        <button
          type="button"
          onClick={onToggleExpandida}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
        >
          <span className="p-2 bg-indigo-500/20 rounded-xl text-indigo-400 flex-shrink-0 text-base leading-none flex items-center justify-center w-8 h-8">
            {tipoMeta.icon}
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-white truncate">{template.nombre || "Plantilla sin nombre"}</h3>
            <p className="text-xs text-gray-500 truncate">{tipoMeta.label} · {template.urlBase || "Sin URL Base configurada"}</p>
          </div>
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          {template.dataCenter && (
            <span className="hidden sm:flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
              {dataCenterLabel(template.dataCenter)}
            </span>
          )}

          <button
            type="button"
            onClick={onGuardar}
            disabled={guardando}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-50 transition-colors"
            title="Guardar plantilla"
          >
            {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {guardando ? "Guardando..." : "Guardar"}
          </button>

          <button
            type="button"
            onClick={onEliminar}
            className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Eliminar plantilla"
          >
            <Trash2 className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={onToggleExpandida}
            className="p-2 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            title={expandida ? "Minimizar" : "Expandir"}
          >
            {expandida ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expandida && (
        <div className="px-5 pb-5">
          <ApiTemplateForm template={template} onChange={onChange} />
        </div>
      )}
    </div>
  );
}
